import React from 'react';
import {Platform, View} from 'react-native';

import Screen from '../../../shared/components/screen';

import {Props} from './model';
import {useStyles} from '../../../shared/hooks/styles';
import {DeviceSize} from '../../../shared/theme/helpers';
import {PollResponsePayload} from '../../../shared/models/api';
import {PollsTable} from '../components/pollsTable';
import {Button} from '../../../shared/components/button';
import {ResponsiveView} from '../../../shared/components/responsiveView';
import {PollsCards} from '../components/pollsCards';
import {Screens} from '../../../shared/routing';
import {useNavigation} from '@react-navigation/native';
import {Container} from '../../../shared/components/container';
import {images} from '../../../assets/icons';
import {hexToRgbA} from '../../../shared/pipes/color';
import {Text} from '../../../shared/components/text';
import {LoadableContent} from '../../../shared/components/loadableContent';
import {useSelector} from 'react-redux';
import {contentSelector} from '../../../redux/selectors/resources';
import {CustomScrollView} from '../../../shared/components/scrollView';
import {useIsDesktop} from '../../../shared/hooks/useIsDesktop';
import {Hr} from '../../../shared/components/hr';

export default ({polls, votes}: Props) => {
  const isDesktop = useIsDesktop();
  const nav = useNavigation();
  const content = useSelector(contentSelector());
  const styles = useStyles(({theme, mediaOnly, platformOnly}) => ({
    sectionContainer: {
      ...mediaOnly(DeviceSize.desktop, {paddingTop: 40, paddingBottom: 40}, {paddingTop: 30, paddingBottom: 30}),
      position: 'relative',
      zIndex: 1,
    },
    sectionSecondBackground: {
      ...mediaOnly(DeviceSize.desktop, {
        backgroundColor: hexToRgbA(theme.color.primaryNonGreyBackground, 0.5),
        position: 'relative',
        zIndex: 1,
      }),
      ...platformOnly('web', {paddingBottom: theme.screen.bottomOffset}),
    },
    sectionTitle: {
      ...theme.fonts.h2,
      marginBottom: 40,
    },
    subSectionTitle: {
      ...mediaOnly(DeviceSize.desktop, theme.fonts.h3, {...theme.fonts.h6, textAlign: 'center'}),
      marginBottom: 28,
    },
    contentWrapper: {
      //...mediaOnly(DeviceSize.desktop, {width: 1100, maxWidth: '100%'}),
    },
    loadMoreButtonWrapper: {
      ...mediaOnly(DeviceSize.desktop, {marginTop: 30}, {marginTop: 10}),
      width: '100%',
      alignItems: 'center',
    },
    loadMoreButton: {
      ...mediaOnly(DeviceSize.mobile, {width: '100%'}, {width: 203}),
      marginTop: 0,
      marginBottom: 0,
    },
    wrapper: {
      position: 'relative',
    },
    bg: {
      position: 'absolute',
      ...mediaOnly(
        DeviceSize.desktop,
        {
          width: 1003,
          height: 1004,
          top: -224,
          right: -350,
        },
        {
          width: 600,
          height: 600,
          top: 100,
          right: -250,
        },
      ),
      zIndex: 0,
    },
    noData: {
      ...mediaOnly(DeviceSize.desktop, theme.fonts.h4Light, {...theme.fonts.additionalText, textAlign: 'center'}),
      color: theme.privilegeNoData.color,
    },
  }));

  const ListRender = ({
    data,
    showAll: {screen},
    emptyMessage,
    testIdPrefix,
  }: {
    data: PollResponsePayload[] | null;
    testIdPrefix: string;
    showAll: {screen: Screens};
    emptyMessage: string;
  }) => {
    const dataRender = data ? (
      data.length === 0 ? (
        <View testID={`${testIdPrefix}-content-id`} style={styles.contentWrapper}>
          <Text testID={`${testIdPrefix}-empty-id`} style={styles.noData}>
            {emptyMessage}
          </Text>
        </View>
      ) : (
        <>
          <ResponsiveView exclude={[DeviceSize.desktop]}>
            <PollsCards polls={data} />
          </ResponsiveView>
          <ResponsiveView only={[DeviceSize.desktop]}>
            <PollsTable polls={data} />
          </ResponsiveView>
        </>
      )
    ) : null;
    return (
      <View style={styles.contentWrapper}>
        {dataRender}
        <View style={styles.loadMoreButtonWrapper}>
          <Button
            id='polls-all'
            onPress={() => {
              nav.navigate(screen);
            }}
            style={styles.loadMoreButton}
            text='Показать все'
            type='secondary'
          />
        </View>
      </View>
    );
  };

  const contentRender = (
    <>
      <Container style={styles.sectionContainer}>
        <View testID='polls-voting-container-id'>
          <ResponsiveView only={[DeviceSize.desktop]}>
            <Text testID='polls-voting-title-id' style={styles.sectionTitle}>
              Опросы и голосования
            </Text>
          </ResponsiveView>
          <Text testID='polls-title-id' style={styles.subSectionTitle}>
            Опросы
          </Text>
          <LoadableContent loading={!polls}>
            <ListRender
              data={polls}
              testIdPrefix='polls-list'
              showAll={{screen: Screens.PollsTypeSurvey}}
              emptyMessage={content.polls.noSurveysOnMainPage}
            />
          </LoadableContent>
        </View>
      </Container>
      <View style={styles.sectionSecondBackground}>
        {!isDesktop && (
          <View style={{paddingHorizontal: 16}}>
            <Hr marginTop={0} marginBottom={0} />
          </View>
        )}
        <Container style={[styles.sectionContainer]}>
          <View>
            <Text testID='voting-title-id' style={styles.subSectionTitle}>
              Голосования
            </Text>
            <LoadableContent loading={!votes}>
              <ListRender
                data={votes}
                testIdPrefix='votes-list'
                showAll={{screen: Screens.PollsTypeVote}}
                emptyMessage={content.polls.noVotesOnMainPage}
              />
            </LoadableContent>
          </View>
        </Container>
      </View>
      {isDesktop && (
        <View style={styles.bg}>
          <images.ShapesPollsBg width={styles.bg.width} height={styles.bg.height} />
        </View>
      )}
    </>
  );

  const wrappedContent =
    Platform.OS !== 'web' ? (
      <CustomScrollView style={styles.wrapper} contentInsetAdjustmentBehavior='automatic'>
        {contentRender}
      </CustomScrollView>
    ) : (
      contentRender
    );

  return <Screen fullWidth>{wrappedContent}</Screen>;
};
